import { Button } from '@mantine/core';
import { FC, useState } from 'react';

import EternoChip from '@/components/EternoChip.tsx';

import { DailyAppointment } from '@/models/appointment/Appointment.ts';
import FormattedForm from '@/models/forms/FormattedForm.ts';
import { FormKeyObject } from '@/models/forms/backend-types/FormDataAppointment.ts';
import { FormDataCode } from '@/models/forms/backend-types/FormDataCode.ts';
import { FormTemplate } from '@/models/forms/backend-types/form-template/FormTemplate.ts';

import { generateCode, saveSelectedFormsForAppointment } from '@/services/formDataService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

import { getSelectedLocation } from '@/utils/configUtils.ts';
import { getCustomerId } from '@/utils/customer-utils.ts';
import {
    filterAndFormatFormsToSave,
    getFormNameFromFormattedForm,
    prepareGenerateCodeBody,
    toggleSelectedForm,
} from '@/utils/formUtils.ts';
import { getUnregisteredUserId } from '@/utils/patientUtils.ts';
import { cn } from '@/utils/styleUtils.ts';

interface Props {
    appointment: DailyAppointment;
    professional_form_templates: FormTemplate[];
    formatted_forms_for_appointment: FormattedForm[];
    onSave: () => Promise<void>;
    onCancel: () => void;
    form_data_code: FormDataCode | undefined;
}

const EditSelectedForms: FC<Props> = ({
    appointment,
    professional_form_templates,
    formatted_forms_for_appointment,
    onSave,
    onCancel,
    form_data_code,
}) => {
    const unclickable_button_fix = 'pb-[2px]'; // The issue happens when the items on the list have a very specific number of characters. Since the button has translationY animation, I just added some space to the bottom to prevent the button from being unclickable.

    const [is_saving, setIsSaving] = useState(false);
    const [selected_forms, setSelectedForms] = useState<string[]>(
        formatted_forms_for_appointment?.map((form) => form.id) ?? []
    );

    const handleSaveForms = async (): Promise<void> => {
        setIsSaving(true);
        const customer_id = getCustomerId();
        const forms_to_save: FormKeyObject[] = filterAndFormatFormsToSave(professional_form_templates, selected_forms);
        const saved_forms = await saveSelectedFormsForAppointment(customer_id, {
            appointment_id: appointment.id,
            user_id: getUnregisteredUserId(appointment),
            data: {
                forms: forms_to_save,
            },
        });

        if (!saved_forms || forms_to_save.length === 0) {
            await onSave();
            setIsSaving(false);
            return;
        }

        const selected_location = getSelectedLocation();
        const generate_code_body = prepareGenerateCodeBody(
            selected_location,
            appointment,
            forms_to_save,
            form_data_code?.code
        );

        if (!generate_code_body) {
            await onSave();
            setIsSaving(false);
            return;
        }

        const code = await generateCode(customer_id, generate_code_body);
        await onSave();

        if (!code) {
            showErrorNotification('Es ist ein Problem bei der Erstellung des QR-Codes aufgetreten.');
        }

        setIsSaving(false);
    };

    return (
        <div className="flex w-full flex-wrap gap-4">
            {professional_form_templates?.map((form) => (
                <EternoChip
                    key={form.id}
                    checked={selected_forms.includes(form.id)}
                    onChange={() => setSelectedForms((forms) => toggleSelectedForm(forms, form.id))}
                    label={getFormNameFromFormattedForm(form)}
                />
            ))}
            <div className={cn('flex w-full justify-between gap-2', unclickable_button_fix)}>
                <Button className="w-1/2" disabled={is_saving} onClick={onCancel} variant="outline">
                    Abbrechen
                </Button>
                <Button className="w-1/2" loading={is_saving} onClick={handleSaveForms}>
                    Speichern
                </Button>
            </div>
        </div>
    );
};

export default EditSelectedForms;
